import { createColumnHelper } from "@tanstack/react-table";
import clsx from "clsx";

import CompanyLogo from "~/components/CompanyLogo";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";
import { customArrayIncludesSome } from "~/utils/tanStackTableUtils.ts";

import type { QuotasIndexTableData } from "./quotasIndexTableTypes.ts";
import { getQuotaStatusIcon, getQuotaStatusOrder } from "./quotasIndexUtils.tsx";

const columnHelper = createColumnHelper<QuotasIndexTableData>();

export const quotasIndexTableColumnDefs = [
	columnHelper.accessor("status", {
		header: "Status",
		meta: {
			cellTextAlign: "center",
			width: "4rem",
		},
		cell: (props) =>
			<div className="flex items-center justify-center">{getQuotaStatusIcon(props.getValue(), "size-6")}</div>,
		filterFn: customArrayIncludesSome,
		sortingFn: (rowA, rowB) =>
			getQuotaStatusOrder(rowA.original.status) - getQuotaStatusOrder(rowB.original.status),
		enableGlobalFilter: false,
	}),
	columnHelper.accessor("companyShortName", {
		header: "Kunde",
		meta: {
			cellTextAlign: "center",
			width: "8rem",
		},
		cell: (props) => {
			const companyShortName = props.getValue();
			const rowData = props.row.original;
			if (!companyShortName) return EMPTY_VALUE_PLACEHOLDER;
			return <div className="flex items-center gap-x-2">
				{rowData.companyAvatarImage && <CompanyLogo image={rowData.companyAvatarImage}
															sizeClassName="size-6" />}
				<span>{companyShortName}</span>
			</div>;
		},
		filterFn: (row, _, value) => {
			if (!Array.isArray(value)) {
				return false;
			}
			if (value.length === 0) {
				return true;
			}
			return value.includes(row.original.companyId);
		},
		enableGlobalFilter: false,
	}),
	columnHelper.accessor("title", {
		header: "Titel",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
		// Carries the global search; the view's globalFilterFn matches row.original.searchString.
	}),

	columnHelper.accessor("amountCents", {
		header: "Betrag",
		meta: {
			headerTextAlign: "right",
			cellTextAlign: "right",
			width: "10rem",
		},
		cell: (props) => formatCentsToCurrency(props.getValue()),
		enableGlobalFilter: false,
	}),
	columnHelper.accessor("consumedPercent", {
		header: "Verbraucht",
		meta: {
			headerTextAlign: "right",
			cellTextAlign: "right",
			width: "8rem",
		},
		cell: (props) => {
			const formattedValue = `${formatNumberWithComma(props.getValue(), 1, true)} %`;
			const width = props.getValue() > 100 ? 100 : props.getValue();
			return <div className="flex items-center justify-end">
				<span className="mr-2">{formattedValue}</span>
				<div className="h-2 w-6 rounded-full bg-gray-300">
					<div className={clsx("h-2 rounded-full",
						width === 100 ? "bg-red-500" :
							width >= 90 ? "bg-yellow-500" :
								"bg-success-500")}
						 style={{ width: `${width}%` }} />
				</div>
			</div>;
		},
		enableGlobalFilter: false,
	}),
	columnHelper.accessor("managerFullName", {
		header: "Verantwortlich",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "12rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
		enableGlobalFilter: false,
	}),
];
